import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './firebaseConfig';
import Lenis from 'lenis';
import 'lenis/dist/lenis.css';

// Pagine
import HomePage from './pages/HomePage';
import RoomsPage from './pages/RoomsPage';
import RoomDetailPage from './pages/RoomDetailPage';
import ServicesPage from './pages/ServicesPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import GalleryPage from './pages/GalleryPage';
import ContactPage from './pages/ContactPage';
import LocationPage from './pages/LocationPage';
import AdminLoginPage from './pages/AdminLoginPage';
import NotFoundPage from './pages/NotFoundPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import ExperiencesPage from './pages/ExperiencesPage';
import ExperienceDetailPage from './pages/ExperienceDetailPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import RoomConfigurationPage from './pages/RoomConfigurationPage';
import AdminPageBuilder from './pages/AdminPageBuilder';
import DynamicPage from './pages/DynamicPage';

// Componenti Layout & Globali
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import LoadingSpinner from './components/LoadingSpinner';
import SeasonalEffect from './components/SeasonalEffect';
import CookieConsentBanner from './components/CookieConsentBanner';
import HomePageLoader from './components/HomePageLoader';
import WeatherWidget from './components/WeatherWidget';
// useLocation is imported above
import './App.css';

// Componente per mostrare il loader globale ad ogni cambio pagina
const GlobalRouteLoader = () => {
    const location = useLocation();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        const timer = setTimeout(() => { setLoading(false); }, 1000);
        return () => clearTimeout(timer);
    }, [location.pathname]);

    return <HomePageLoader show={loading} siteName="L'Antico Piol De Feltre" />;
};

// Componente per transizione tra pagine (fade out a bianco)
const PageTransitionLayer = () => {
    const navigate = useNavigate();
    const [isTransitioning, setIsTransitioning] = useState(false);

    useEffect(() => {
        const handleClick = (e) => {
            const link = e.target.closest('a');
            if (link && link.href) {
                const url = new URL(link.href);
                if (url.origin === window.location.origin && link.target !== '_blank' && !link.hasAttribute('download')) {
                    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
                    
                    const path = url.pathname + url.search + url.hash;
                    if (path !== window.location.pathname + window.location.search + window.location.hash) {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        setIsTransitioning(true);
                        setTimeout(() => {
                            navigate(path);
                            setIsTransitioning(false);
                            window.scrollTo(0, 0);
                        }, 500);
                    }
                }
            }
        };

        document.addEventListener('click', handleClick, true);
        return () => document.removeEventListener('click', handleClick, true);
    }, [navigate]);

    return (
        <div 
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: '#ffffff',
                zIndex: 999999,
                opacity: isTransitioning ? 1 : 0,
                pointerEvents: isTransitioning ? 'all' : 'none',
                transition: 'opacity 0.5s ease-in-out'
            }}
        />
    );
};

// Icona Admin Globale
const AdminPencilIcon = () => {
    const { currentUser } = useAuth();
    if (!currentUser) return null;
    return <div className="admin-pencil-icon" title="Modalità Modifica Attiva">✏️</div>;
};

// Componente App principale
function App() {
    const [settingsLoading, setSettingsLoading] = useState(true);

    useEffect(() => {
        const settingsDocRef = doc(db, 'site_settings', 'global');
        const unsubscribe = onSnapshot(settingsDocRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                if (data.siteName) { document.title = data.siteName; }
                const faviconTag = document.querySelector("link[rel~='icon']");
                if(faviconTag && data.siteLogoUrl && (data.siteLogoUrl.startsWith('http') || data.siteLogoUrl.startsWith('/'))) {
                    faviconTag.href = data.siteLogoUrl;
                }
            } else {
                 console.log("Documento impostazioni globali non trovato in App, uso i default HTML.");
            }
             setSettingsLoading(false);
        }, (error) => {
            console.error("Errore listener impostazioni in App:", error);
             setSettingsLoading(false);
        });
        return () => unsubscribe();
    }, []);


    useEffect(() => {
        const lenis = new Lenis({
            autoRaf: true, // Automatically handles requestAnimationFrame
        });

        return () => {
            lenis.destroy();
        };
    }, []);

    return (
        <AuthProvider>
            <LoadingSpinner show={settingsLoading} />
            <Router>
                <PageTransitionLayer />
                <GlobalRouteLoader />
                <ScrollToTop />
                <SeasonalEffect />
                <AdminPencilIcon />
                <Navbar />
                <main className="main-content">
                    <Routes>
                         <Route path="/" element={<HomePage />} />
                         <Route path="/camere" element={<RoomsPage />} />
                         <Route path="/camere/:roomId" element={<RoomDetailPage />} />
                         <Route path="/servizi" element={<ServicesPage />} />
                         <Route path="/servizi/:serviceId" element={<ServiceDetailPage />} />
                         <Route path="/esperienze" element={<ExperiencesPage />} /> 
                         <Route path="/esperienze/:experienceId" element={<ExperienceDetailPage />} /> 
                         <Route path="/galleria" element={<GalleryPage />} />
                         <Route path="/posizione" element={<LocationPage />} />
                         <Route path="/contatti" element={<ContactPage />} />
                         <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                         <Route path="/termini-e-condizioni" element={<TermsConditionsPage />} />
                         <Route path="/configura-camera" element={<RoomConfigurationPage />} />
                         <Route path="/admin-login-segreto-xyz123" element={<AdminLoginPage />} />
                         <Route path="/admin-impostazioni-sito" element={<AdminSettingsPage />} />
                         <Route path="/admin-page-builder/*" element={<AdminPageBuilder />} />
                         {/* Fallback routes - DynamicPage handles the catch-all and will show NotFound if doc doesn't exist */}
                         <Route path="*" element={<DynamicPage />} />
                    </Routes>
                </main>
                <Footer />
                <CookieConsentBanner />
                <WeatherWidget />
            </Router>
        </AuthProvider>
    );
}

export default App;